module Service

open Fable
open Browser.Dom
open Fable.Core
open Fetch.Types
open Thoth.Json
open Elmish.UrlBuilder.Builder
open Dotyk.Portal.Model
open Fable.Core.JsInterop

let private getBody (response : Response) =
    response.json()

let private unwrapResult result =
    match result with
    | Ok (Ok v) -> Ok v
    | Error e 
    | Ok (Error e) -> Error e

let private parse decoder =
    (Decode.fromValue "" decoder)
    >> (Result.mapError exn)

let private throwOnError result =
    match result with
    | Ok v -> v
    | Error e -> raise e

let private getOpts =
    [ Method HttpMethod.GET
      Fetch.requestHeaders [ Accept "application/json" ] ]

let private postOpts =
    [ Method HttpMethod.POST
      Fetch.requestHeaders [ ContentType "application/json" ] ]
      
let private deleteOpts =
    [ Method HttpMethod.DELETE
      Fetch.requestHeaders [ ContentType "application/json" ] ]
      
let private processResult decoder result  =
    result
    |> Promise.bindResult getBody
    |> Promise.mapResult (parse decoder)
    |> Promise.map (unwrapResult >> throwOnError)

let getJson<'t> url (decoder:Decoder<'t>) =
    Fetch.tryFetch
        ("api/portal/" + url)
        getOpts 
    |> processResult decoder

let postJson<'req, 'res> url body (encoder:Encoder<'req>) (decoder:Decoder<'res>) =
    let bodyJson =
        body
        |> encoder 
        |> Encode.toString 0 

    Fetch.tryFetch
        ("api/portal/" + url)
        ((Body !^bodyJson)::postOpts)
    |> processResult decoder

let postEmptyJson<'req, 'res> url (decoder:Decoder<'res>) =
    Fetch.tryFetch
        ("api/portal/" + url)
        (postOpts)
    |> processResult decoder

let deleteJson<'req, 'res> url body (encoder:Encoder<'req>) (decoder:Decoder<'res>) =
    let bodyJson =
        body
        |> encoder 
        |> Encode.toString 0 

    Fetch.tryFetch
        ("api/portal/" + url)
        ((Body !^bodyJson)::deleteOpts)
    |> processResult decoder

type TokenItem = {
    token : string
}

let private tokenItemDecoder = Decode.Auto.generateDecoderCached<TokenItem>()
let private appListDecoder = Decode.Auto.generateDecoderCached<ApplicationInfo list>()
let private languagesDecoder = Decode.Auto.generateDecoderCached<string>()
let private accessInfoDecoder = Decode.Auto.generateDecoderCached<AccessInfo>()
let private accessItemEncoder = Encode.Auto.generateEncoderCached<UserAccess>()
let private locationsDecoder = Decode.Auto.generateDecoderCached<LocationInfo list>()
let private subscriptions = Decode.Auto.generateDecoderCached<SubscriptionInfo list>()
let private iamObjectId = Decode.field "iamId" Decode.string

open Global

let private pageInfoDecoder = Decode.Auto.generateDecoderCached<ExternalPageInfo>();

let private externalPagesDecoder =
    Decode.object (
        fun get ->
            {
                pages = pageInfoDecoder
                        |> Decode.list
                        |> get.Optional.Field "pages" 
                        |> Option.defaultValue [] 
            })

let getToken url =
    getJson
        (relative [ "token" ] [ str "appUrl" url])
        tokenItemDecoder

let getLocations () =
    getJson
        (relative ["locations"] [])
        locationsDecoder
    |> Promise.map (List.sortBy (fun i -> i.name))

let getApps locId=
    getJson
        (relative [ "locations"; locId; "apps"] [])
        appListDecoder
    |> Promise.map (List.sortBy (fun i -> i.name))
    
let getLangs locId=
    getJson
        (relative [ "locations"; locId; "langs"] [])
        languagesDecoder

let getAccess app =
    getJson
        (relative ["access"] [ str "url" app ])
        accessInfoDecoder

let removeAccess app access =
    deleteJson
        (relative ["access"] [ str "url" app ])
        access
        accessItemEncoder
        accessInfoDecoder

let addAccess app access =
    postJson
        (relative ["access"] [ str "url" app ])
        access
        accessItemEncoder
        accessInfoDecoder

let getPages () =
    getJson
        (relative ["pages"] [] )
        externalPagesDecoder

let getAvailableSubs app =
    getJson
        (relative ["iam"; "subscriptions"; "forCreation"] [ str "appUrl" app ])
        subscriptions

let connectIam app sub =
    postEmptyJson
        (relative ["iam"; "connect"] [ str "url" app; str "subId" sub])
        iamObjectId

let iamEditorUrl =
    promise {
        let! resp =
            Fetch.tryFetch
                ("api/portal/" + (relative ["pages"; "iam"; "editor"] []))
                getOpts

        let res = 
            match resp with
            | Error _ -> None
            | Ok resp -> 
                if (resp.Status = 204)
                then
                    None
                else
                    Some (resp.text())

        match res with
        | None -> return None
        | Some p ->
            let! url = p
            return (Some (url.Trim('"')))
    }
